import { createFileRoute, useRouter } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { SiteShell } from "@/components/site/SiteShell";
import { LeaderboardView } from "@/components/audit/LeaderboardView";
import { getLeaderboard } from "@/lib/audit.functions";
import { SITE_URL } from "@/lib/i18n";
import { buildHead } from "@/lib/seo";

const leaderboardQueryOptions = (locale: "en" | "es") =>
  queryOptions({
    queryKey: ["leaderboard", locale],
    queryFn: () => getLeaderboard({ data: { locale } }),
  });

export const Route = createFileRoute("/es/auditoria/tabla")({
  loader: ({ context }) => context.queryClient.ensureQueryData(leaderboardQueryOptions("es")),
  head: ({ loaderData }) => {
    const title = "Tiendas Shopify más legibles por IA: Ranking Shugert";
    const description =
      "Las tiendas Shopify con mejores resultados en nuestra auditoría gratuita de Legibilidad por IA (AEO), qué tan bien los motores de respuestas AI las encuentran, citan y referencian.";
    const path = "/es/auditoria/tabla";
    const entries = loaderData?.entries ?? [];
    const itemList = entries.length
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: title,
          numberOfItems: entries.length,
          itemListElement: entries.map((e) => ({
            "@type": "ListItem",
            position: e.rank,
            url: `${SITE_URL}/es/auditoria/resultados/${e.slug}`,
            name: e.hostname,
          })),
        }
      : undefined;
    return buildHead({
      locale: "es",
      title,
      description,
      path,
      altPath: "/audit/leaderboard",
      jsonLd: itemList,
    });
  },
  errorComponent: ({ reset }) => {
    const router = useRouter();
    return (
      <SiteShell>
        <div className="mx-auto max-w-2xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-semibold">Algo salió mal</h1>
          <p className="mt-3 text-muted-foreground">No pudimos cargar el ranking en este momento.</p>
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="mt-6 inline-flex rounded-lg border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:border-foreground/40"
          >
            Reintentar
          </button>
        </div>
      </SiteShell>
    );
  },
  notFoundComponent: () => (
    <SiteShell>
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="font-display text-3xl font-semibold">Ranking no disponible</h1>
      </div>
    </SiteShell>
  ),
  component: function AuditoriaTablaPage() {
    const { data } = useSuspenseQuery(leaderboardQueryOptions("es"));
    return (
      <SiteShell>
        <LeaderboardView data={data} locale="es" />
      </SiteShell>
    );
  },
});